import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Table, Accordion, Spinner } from 'react-bootstrap';
import ApiService from 'helpers/apiService';
import QuestionnaireCard from './QuestionnaireCard';
import RiskScoreCard, { RiskScoreData } from './RiskScoreCard';

type KycUser = {
  id?: string;
  user_id?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  dob?: string;
  country?: string;
  phone?: string;
  gender?: string;
  status?: string;
  review_status?: string;
};

type KycScore = {
  score?: number;
  documents?: Array<{ name?: string; status?: string; score?: number }>;
  review_status?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  dob?: string;
  country?: string;
  phone?: string;
  risk?: RiskScoreData | null;
  risk_labels?: string[];
};

const STATUS_BADGE: Record<string, string> = {
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  PENDING: 'secondary',
  INIT: 'secondary',
};

const statusBadge = (status?: string) => {
  if (!status) return null;
  const bg = STATUS_BADGE[status] ?? 'info';
  const textDark = bg === 'warning';
  return (
    <Badge bg={bg} text={textDark ? 'dark' : undefined}>
      {status}
    </Badge>
  );
};

const FieldRow = ({ label, value }: { label: string; value?: string }) => (
  <div className="col-12 col-sm-6 col-md-4">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{value || '—'}</div>
  </div>
);

const Kyc = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<KycUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<KycUser | null>(null);
  const [score, setScore] = useState<KycScore | null>(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState<string | null>(null);
  const [rejectLoading, setRejectLoading] = useState<string | null>(null);
  const [refreshLoading, setRefreshLoading] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const userId = selectedUser?.user_id ?? selectedUser?.id ?? '';

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ApiService.kycListUser<KycUser[]>();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Ошибка загрузки списка KYC:', e);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScore = useCallback(async (uid: string) => {
    if (!uid) return;
    setScoreLoading(true);
    setScore(null);
    try {
      const data = await ApiService.kycGetScore<KycScore>({ user_id: uid });
      setScore(data ?? null);
    } catch (e) {
      console.error('Ошибка загрузки скора:', e);
      setScore(null);
    } finally {
      setScoreLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (userId) loadScore(userId);
  }, [userId, loadScore]);

  const handleAccordionSelect = (key: string | string[] | null | undefined) => {
    const newKey = typeof key === 'string' ? key : null;
    setActiveKey(newKey);
    if (newKey !== null) {
      const index = parseInt(newKey, 10);
      if (!isNaN(index) && users[index]) {
        setSelectedUser(users[index]);
      }
    } else {
      setSelectedUser(null);
      setScore(null);
    }
  };

  const handleReject = async (user: KycUser) => {
    const uid = user.user_id ?? user.id;
    if (!uid) return;
    setRejectLoading(uid);
    try {
      await ApiService.kycRejectUser({ user_id: uid });
      setActiveKey(null);
      setSelectedUser(null);
      setScore(null);
      await loadUsers();
    } catch (e) {
      console.error('Ошибка отклонения:', e);
    } finally {
      setRejectLoading(null);
    }
  };

  const handleRefresh = async (user: KycUser) => {
    const uid = user.user_id ?? user.id;
    if (!uid) return;
    setRefreshLoading(uid);
    try {
      await ApiService.kycRefreshApplicant(uid);
      // После refresh подтягиваем заново скор + список (карточка раскрыта)
      await loadScore(uid);
      await loadUsers();
    } catch (e) {
      console.error('Ошибка обновления данных SumSub:', e);
    } finally {
      setRefreshLoading(null);
    }
  };

  const handleConfirm = async (user: KycUser) => {
    const uid = user.user_id ?? user.id;
    if (!uid) return;
    setConfirmLoading(uid);
    try {
      await ApiService.kycConfirmUser({ user_id: uid });
      setActiveKey(null);
      setSelectedUser(null);
      setScore(null);
      await loadUsers();
    } catch (e) {
      console.error('Ошибка подтверждения:', e);
    } finally {
      setConfirmLoading(null);
    }
  };

  const displayName = (user: KycUser) =>
    user.name || [user.last_name, user.first_name, user.middle_name].filter(Boolean).join(' ') || user.user_id || user.id || '—';

  const onHoldCount = users.filter(u => (u.review_status || u.status) === 'ON_HOLD').length;
  const completedCount = users.filter(u => (u.review_status || u.status) === 'COMPLETED').length;

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">KYC — Верификация</h3>
              <p className="mt-1 mb-0 text-muted small">Заявки на проверку личности</p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg="warning" text="dark" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                Ожидают: {onHoldCount}
              </Badge>
              <Badge bg="success" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                Подтверждено: {completedCount}
              </Badge>
              <Badge bg="secondary" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
                Всего: {users.length}
              </Badge>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center p-5">
              <Spinner animation="border" />
              <span className="ms-2">Загрузка списка...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="p-4 text-muted text-center">Нет заявок KYC для проверки.</div>
          ) : (
            <Accordion activeKey={activeKey} onSelect={handleAccordionSelect}>
              {users.map((user, index) => {
                const uid = user.user_id ?? user.id;
                const eventKey = String(index);
                const isSelected = activeKey === eventKey;
                const reviewStatus = user.review_status || user.status;
                return (
                  <Accordion.Item eventKey={eventKey} key={uid ?? index}>
                    <Accordion.Header>
                      <div className="d-flex align-items-center justify-content-between w-100 me-2">
                        <span className="fw-semibold" style={{ fontSize: '0.875rem' }}>{displayName(user)}</span>
                        <div className="d-flex align-items-center gap-2">
                          {user.country && (
                            <span className="text-muted small">{user.country}</span>
                          )}
                          {statusBadge(reviewStatus)}
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-3">
                      {isSelected && (
                        <>
                          {/* Personal Info */}
                          <div className="row g-2 mb-3">
                            <FieldRow label="Фамилия" value={user.last_name || score?.last_name} />
                            <FieldRow label="Имя" value={user.first_name || score?.first_name} />
                            <FieldRow label="Отчество" value={user.middle_name || score?.middle_name} />
                            <FieldRow label="Дата рождения" value={user.dob || score?.dob} />
                            <FieldRow label="Страна" value={user.country || score?.country} />
                            <FieldRow label="Телефон" value={user.phone || score?.phone} />
                            {user.gender && <FieldRow label="Пол" value={user.gender} />}
                          </div>

                          {/* Documents table */}
                          <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th>Документ</th>
                                <th>Статус</th>
                                <th style={{ width: 80 }}>Баллы</th>
                              </tr>
                            </thead>
                            <tbody>
                              {scoreLoading ? (
                                <tr>
                                  <td colSpan={3} className="text-center py-3">
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Загрузка...
                                  </td>
                                </tr>
                              ) : score?.documents?.length ? (
                                score.documents.map((doc, i) => (
                                  <tr key={i}>
                                    <td>{doc.name ?? '—'}</td>
                                    <td>{doc.status ?? '—'}</td>
                                    <td>{doc.score ?? score?.score ?? '—'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={3} className="text-muted py-2">
                                    {score != null && typeof score.score === 'number'
                                      ? `Общий скор: ${score.score}`
                                      : 'Нет данных по документам'}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>

                          {/* Risk scoring (SumSub mock 2026-05-07) */}
                          <RiskScoreCard
                            risk={score?.risk}
                            labels={score?.risk_labels}
                            defaultOpen={true}
                          />

                          {/* Questionnaire (SumSub SDK form answers) */}
                          {uid && <QuestionnaireCard userId={uid} defaultOpen={true} />}

                          {/* Actions */}
                          <div className="d-flex justify-content-end mt-3 gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={refreshLoading === uid}
                              onClick={() => handleRefresh(user)}
                              title="Перезапросить данные у SumSub и обновить в БД"
                            >
                              {refreshLoading === uid ? (
                                <><Spinner animation="border" size="sm" className="me-1" />Обновление...</>
                              ) : 'Обновить данные SumSub'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={rejectLoading === uid}
                              onClick={() => handleReject(user)}
                            >
                              {rejectLoading === uid ? (
                                <><Spinner animation="border" size="sm" className="me-1" />Отклонение...</>
                              ) : 'Отклонить'}
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              disabled={confirmLoading === uid}
                              onClick={() => handleConfirm(user)}
                            >
                              {confirmLoading === uid ? (
                                <><Spinner animation="border" size="sm" className="me-1" />Подтверждение...</>
                              ) : 'Подтвердить'}
                            </Button>
                          </div>
                        </>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Kyc;
